// src/components/common/StockEntryModal/StockEntryModal.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../../firebaseConfig';
import {
    collection,
    doc,
    runTransaction,
    serverTimestamp,
    getDocs,
    query,
    where,
    getDoc
} from 'firebase/firestore';
import {
    FiX,
    FiSave,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiPackage,
    FiMapPin,
    FiTag,
    FiCalendar,
    FiInfo,
    FiChevronDown,
    FiChevronUp
} from 'react-icons/fi';
import { FaWater } from 'react-icons/fa';
import styles from './StockEntryModal.module.css';

const CURRENT_STOCK_COLLECTION = 'current-stock';
const PRODUCTS_COLLECTION = 'products';
const LOCATIONS_COLLECTION = 'localizacoes';
const STOCK_ENTRIES_COLLECTION = 'stock-entries';

const isWaterMaterial = (material) => {
    if (!material) return false;
    const name = (material.name || material.inciName || material.commercialName || '').toUpperCase();
    return name.includes('ÁGUA') || name.includes('AGUA') || name === 'WATER';
};

const StockEntryModal = ({
    isOpen,
    onClose,
    product,
    requestedQuantity,
    onSuccess,
    addToast
}) => {
    const [loading, setLoading] = useState(false);
    const [locations, setLocations] = useState([]);
    const [selectedLocationId, setSelectedLocationId] = useState('');
    const [quantity, setQuantity] = useState(requestedQuantity || 0);
    const [lotNumber, setLotNumber] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [manufacturerDate, setManufacturerDate] = useState('');
    const [notes, setNotes] = useState('');
    const [isLocationOpen, setIsLocationOpen] = useState(false);
    const [locationSearchTerm, setLocationSearchTerm] = useState('');
    const [locationCategoryFilter, setLocationCategoryFilter] = useState('');
    const [isWater, setIsWater] = useState(false);
    const [currentStock, setCurrentStock] = useState(0);
    const [shortage, setShortage] = useState(0);

    const locationInputRef = React.useRef(null);
    const locationDropdownRef = React.useRef(null);

    useEffect(() => {
        if (isOpen && product) {
            fetchLocations();
            fetchCurrentStock();
            setIsWater(isWaterMaterial(product));
            setQuantity(requestedQuantity || 0);
            setShortage(Math.max(0, (requestedQuantity || 0) - currentStock));
        }
    }, [isOpen, product, requestedQuantity]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (locationInputRef.current && !locationInputRef.current.contains(event.target)) {
                if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
                    setIsLocationOpen(false);
                }
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchCurrentStock = async () => {
        if (!product?.id) return;
        try {
            const stockRef = collection(db, CURRENT_STOCK_COLLECTION);
            const q = query(stockRef, where('rawMaterialId', '==', product.id));
            const snapshot = await getDocs(q);
            let total = 0;
            snapshot.forEach(doc => {
                total += (doc.data().quantity || 0);
            });
            setCurrentStock(total);
            setShortage(Math.max(0, (requestedQuantity || 0) - total));
        } catch (error) {
            console.error('Error fetching current stock:', error);
        }
    };

    const fetchLocations = async () => {
        try {
            const locationsRef = collection(db, LOCATIONS_COLLECTION);
            const snapshot = await getDocs(locationsRef);
            const locationsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setLocations(locationsData);
        } catch (error) {
            console.error('Error fetching locations:', error);
            if (addToast) addToast('Erro ao carregar localizações', 'Error');
        }
    };

    const filteredLocations = useMemo(() => {
        let filtered = locations;
        if (locationCategoryFilter) {
            filtered = filtered.filter(loc => loc.category === locationCategoryFilter);
        }
        if (locationSearchTerm.trim()) {
            const searchLower = locationSearchTerm.toLowerCase();
            filtered = filtered.filter(loc =>
                loc.id.toLowerCase().includes(searchLower) ||
                (loc.description && loc.description.toLowerCase().includes(searchLower))
            );
        }
        return filtered;
    }, [locations, locationCategoryFilter, locationSearchTerm]);

    const handleLocationSelect = (location) => {
        setSelectedLocationId(location.id);
        setLocationSearchTerm('');
        setIsLocationOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedLocationId) {
            if (addToast) addToast('Selecione uma localização para o estoque.', 'Warning');
            return;
        }

        if (!quantity || quantity <= 0) {
            if (addToast) addToast('Informe uma quantidade válida.', 'Warning');
            return;
        }

        setLoading(true);
        try {
            await runTransaction(db, async (transaction) => {
                // Generate stock ID
                let stockId;
                if (lotNumber.trim()) {
                    stockId = `${product.id}_${selectedLocationId}_${lotNumber.trim()}`;
                } else {
                    stockId = `${product.id}_${selectedLocationId}`;
                }

                const stockRef = doc(db, CURRENT_STOCK_COLLECTION, stockId);
                const stockDoc = await transaction.get(stockRef);
                const currentQty = stockDoc.exists() ? (stockDoc.data().quantity || 0) : 0;
                const newQuantity = currentQty + parseFloat(quantity);

                // Update or create stock entry
                const stockData = {
                    rawMaterialId: product.id,
                    locationId: selectedLocationId,
                    quantity: newQuantity,
                    rawMaterialCode: product.code || '',
                    rawMaterialName: product.name || product.commercialName || '',
                    locationDescription: locations.find(l => l.id === selectedLocationId)?.description || selectedLocationId,
                    updatedAt: serverTimestamp(),
                    createdAt: stockDoc.exists() ? stockDoc.data().createdAt : serverTimestamp(),
                };

                if (lotNumber.trim()) {
                    stockData.lotNumber = lotNumber.trim();
                    stockData.expirationDate = expirationDate || null;
                    stockData.manufacturerDate = manufacturerDate || null;
                    stockData.isProduct = true;
                    stockData.originalStockId = `${product.id}_${selectedLocationId}`;
                }

                transaction.set(stockRef, stockData);

                // Create entry record
                const entryRef = doc(collection(db, STOCK_ENTRIES_COLLECTION));
                const entryData = {
                    rawMaterialId: product.id,
                    rawMaterialCode: product.code || '',
                    rawMaterialName: product.name || product.commercialName || '',
                    locationIds: [selectedLocationId],
                    locationQuantities: { [selectedLocationId]: parseFloat(quantity) },
                    quantity: parseFloat(quantity),
                    isWater: false,
                    isProduct: true,
                    type: 'ENTRADA',
                    notes: notes.trim() || `Entrada manual via aprovação de pedido - Produto: ${product.code}`,
                    createdAt: serverTimestamp(),
                    createdBy: 'Aprovador - Stock Entry'
                };

                if (lotNumber.trim()) {
                    entryData.lotNumber = lotNumber.trim();
                    entryData.expirationDate = expirationDate || null;
                    entryData.manufacturerDate = manufacturerDate || null;
                }

                transaction.set(entryRef, entryData);
            });

            if (addToast) addToast(`Estoque adicionado com sucesso para ${product.code || product.name}!`, 'Success');
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error adding stock:', error);
            if (addToast) addToast(`Erro ao adicionar estoque: ${error.message}`, 'Error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={`${styles.formContainer} ${styles.formContainerMedium}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FiPackage size={24} style={{ marginRight: '10px' }} /> Adicionar Estoque Manual</h2>
                        <p className={styles.modalSubtitle}>
                            Produto: <strong>{product?.code} - {product?.name || product?.commercialName}</strong>
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className={styles.modalCloseButton} disabled={loading} aria-label="Fechar">
                        <FiX size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.formContent}>
                    {/* Stock Status Info */}
                    <div className={styles.infoBox} style={{ backgroundColor: shortage > 0 ? 'var(--warning-light)' : 'var(--success-light)', borderLeftColor: shortage > 0 ? 'var(--warning-color)' : 'var(--success-color)' }}>
                        <div className={styles.stockStatus}>
                            <div>
                                <span className={styles.stockLabel}>Estoque Atual:</span>
                                <span className={styles.stockValue}>{currentStock.toFixed(3)}</span>
                            </div>
                            <div>
                                <span className={styles.stockLabel}>Necessário:</span>
                                <span className={styles.stockValue}>{requestedQuantity?.toFixed(3) || 0}</span>
                            </div>
                            <div>
                                <span className={styles.stockLabel}>Falta:</span>
                                <span className={styles.stockValue} style={{ color: shortage > 0 ? 'var(--danger-color)' : 'var(--success-color)' }}>
                                    {shortage > 0 ? shortage.toFixed(3) : '0'}
                                </span>
                            </div>
                        </div>
                        {shortage > 0 && (
                            <div className={styles.shortageNote}>
                                <FiAlertCircle size={16} />
                                <span>Adicione a quantidade faltante abaixo.</span>
                            </div>
                        )}
                    </div>

                    {/* Location */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiMapPin size={18} /></div>
                            <h3 className={styles.sectionTitle}>Localização</h3>
                            <span className={styles.sectionBadge}>Obrigatório</span>
                        </div>
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                <label htmlFor="locationCategoryFilter">Categoria da Localização</label>
                                <select
                                    id="locationCategoryFilter"
                                    value={locationCategoryFilter}
                                    onChange={(e) => {
                                        setLocationCategoryFilter(e.target.value);
                                        setSelectedLocationId('');
                                        setLocationSearchTerm('');
                                    }}
                                    className={`${styles.input} ${styles.select}`}
                                    disabled={loading}
                                >
                                    <option value="">Selecione uma categoria...</option>
                                    <option value="mp_semi">MP e Semi Acabado</option>
                                    <option value="embalagem_acabado">Embalagem e Produto Acabado</option>
                                </select>
                            </div>

                            <div className={styles.inputGroup} style={{ gridColumn: 'span 2', position: 'relative' }}>
                                <label>Localização <span className={styles.required}>*</span></label>
                                <div className={styles.searchableSelectWrapper} style={{ position: 'relative' }}>
                                    <input
                                        ref={locationInputRef}
                                        type="text"
                                        className={`${styles.input} ${styles.comboInput}`}
                                        placeholder={locationCategoryFilter ? 'Buscar localização por ID ou descrição...' : 'Selecione uma categoria primeiro'}
                                        value={selectedLocationId || locationSearchTerm}
                                        onChange={(e) => {
                                            setLocationSearchTerm(e.target.value);
                                            if (selectedLocationId) setSelectedLocationId('');
                                            setIsLocationOpen(true);
                                        }}
                                        onFocus={() => {
                                            if (locationCategoryFilter) setIsLocationOpen(true);
                                        }}
                                        disabled={loading || !locationCategoryFilter}
                                    />
                                    <div
                                        className={`${styles.dropdownArrow} ${isLocationOpen ? styles.open : ''}`}
                                        onClick={() => {
                                            if (locationCategoryFilter) {
                                                setIsLocationOpen(!isLocationOpen);
                                            }
                                        }}
                                    >
                                        {isLocationOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                    </div>

                                    {isLocationOpen && locationCategoryFilter && (
                                        <div
                                            ref={locationDropdownRef}
                                            className={styles.optionsDropdownPortal}
                                            style={{
                                                position: 'fixed',
                                                top: locationInputRef.current?.getBoundingClientRect().bottom + window.scrollY || 0,
                                                left: locationInputRef.current?.getBoundingClientRect().left || 0,
                                                width: locationInputRef.current?.offsetWidth || '100%',
                                                zIndex: 10001,
                                                maxHeight: '250px',
                                                overflowY: 'auto',
                                                backgroundColor: 'var(--content-bg)',
                                                border: '2px solid var(--border-color)',
                                                borderRadius: 'var(--border-radius-md)',
                                                boxShadow: 'var(--shadow-lg)'
                                            }}
                                        >
                                            {filteredLocations.length > 0 ? (
                                                filteredLocations.map(location => (
                                                    <div
                                                        key={location.id}
                                                        className={`${styles.optionItem} ${selectedLocationId === location.id ? styles.selected : ''}`}
                                                        onClick={() => handleLocationSelect(location)}
                                                        style={{
                                                            padding: '10px 12px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            borderBottom: '1px solid var(--border-color)',
                                                            backgroundColor: selectedLocationId === location.id ? 'var(--primary-light)' : 'transparent'
                                                        }}
                                                    >
                                                        <div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <span className={styles.optionCode}>{location.id}</span>
                                                                {location.categoryName && (
                                                                    <span style={{ fontSize: '10px', color: '#666' }}>
                                                                        ({location.categoryName})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {location.description && (
                                                                <span style={{ fontSize: '11px', color: '#888' }}>{location.description}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className={styles.noResults} style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                                    Nenhuma localização encontrada
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quantity */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                            <h3 className={styles.sectionTitle}>Quantidade</h3>
                            <span className={styles.sectionBadge}>Obrigatório</span>
                        </div>
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                <label>Quantidade a Adicionar <span className={styles.required}>*</span></label>
                                <div className={styles.quantityWrapper}>
                                    <input
                                        type="number"
                                        step="0.001"
                                        min="0.001"
                                        value={quantity}
                                        onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
                                        className={styles.input}
                                        placeholder="Quantidade a adicionar"
                                        disabled={loading}
                                        required
                                    />
                                    <span className={styles.unitText}>kg/L/un</span>
                                </div>
                                <small className={styles.fieldHint}>
                                    {shortage > 0 && `Falta ${shortage.toFixed(3)} unidades. Recomendado adicionar esta quantidade.`}
                                </small>
                            </div>
                        </div>
                    </div>

                    {/* Traceability */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiTag size={18} /></div>
                            <h3 className={styles.sectionTitle}>Rastreabilidade</h3>
                            <span className={styles.sectionBadge}>Opcional</span>
                        </div>
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup}>
                                <label>Número do Lote</label>
                                <input
                                    type="text"
                                    value={lotNumber}
                                    onChange={(e) => setLotNumber(e.target.value.toUpperCase())}
                                    className={styles.input}
                                    placeholder="Ex: LOTE001, 2024-001"
                                    disabled={loading}
                                    style={{ textTransform: 'uppercase' }}
                                />
                                <small className={styles.fieldHint}>Identificação do lote (opcional)</small>
                            </div>

                            <div className={styles.inputGroup}>
                                <label>Data de Fabricação</label>
                                <input
                                    type="date"
                                    value={manufacturerDate}
                                    onChange={(e) => setManufacturerDate(e.target.value)}
                                    className={styles.input}
                                    disabled={loading}
                                />
                            </div>

                            <div className={styles.inputGroup}>
                                <label>Data de Validade</label>
                                <input
                                    type="date"
                                    value={expirationDate}
                                    onChange={(e) => setExpirationDate(e.target.value)}
                                    className={styles.input}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Notes */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiInfo size={18} /></div>
                            <h3 className={styles.sectionTitle}>Observações</h3>
                            <span className={styles.sectionBadge}>Opcional</span>
                        </div>
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className={styles.textarea}
                                    rows="3"
                                    disabled={loading}
                                    placeholder="Adicione observações sobre esta entrada manual..."
                                />
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className={styles.formActions}>
                        <button type="button" onClick={onClose} className={styles.secondaryButton} disabled={loading}>
                            <FiX size={18} /> Cancelar
                        </button>
                        <button type="submit" className={styles.confirmButton} disabled={loading}>
                            {loading ? (
                                <><FiRefreshCw size={18} className={styles.buttonSpinner} /> Processando...</>
                            ) : (
                                <><FiSave size={18} /> Adicionar Estoque</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default StockEntryModal;